import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowRight, ChevronDown, FileText } from "lucide-react";

export const EMPTY_FORWARDING_NOTE = {
  c1_value_per_head: "", c2_value: "", c2_engage: "engage", c4_ip_status: "has been",
  c5_outer_packing_defect: "", c6_bad_condition: "", c7_commodity: "", c8_rate_election: "",
  c9_route_via: "", c10_route_via: "", c11_mode: "open wagon",
};

const HIDDEN = [1, 2, 3, 9, 10, 11];

function Blank({ k, note, set, w = "w-40", placeholder = "" }) {
  return (
    <input value={note[k]} onChange={(e) => set(k, e.target.value)} placeholder={placeholder} data-testid={`fn-${k}`}
      className={`inline-block ${w} max-w-full mx-1 px-1 border-0 border-b border-dashed border-primary bg-primary/5 text-foreground font-mono-num text-sm focus:outline-none focus:border-solid`} />
  );
}

function Choice({ k, note, set, options }) {
  return (
    <select value={note[k]} onChange={(e) => set(k, e.target.value)} data-testid={`fn-${k}`}
      className="inline-block mx-1 px-1 border-0 border-b border-dashed border-primary bg-primary/5 text-foreground text-sm font-semibold focus:outline-none">
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function clauses(note, set) {
  const p = { note, set };
  return [
    [1, <>I declare the value of these elephants / horses / mules / camels / horned cattle / donkeys / sheep / goats / dogs / other animals and birds to be Rs.<Blank k="c1_value_per_head" w="w-28" {...p} />per head and engage to pay the authorised percentage charge on excess value for the increased risk.</>],
    [2, <>I declare that each consignment is of a value of Rs.<Blank k="c2_value" w="w-28" {...p} />and<Choice k="c2_engage" options={["engage", "do not engage"]} {...p} />to pay the percentage charge on excess value for the increased risk, as required by the Administration.</>],
    [3, <>I declare that the packages tendered with this Forwarding Note contain the undermentioned articles enumerated in Part I of Schedule II of the Railways (Extent of Monetary Liability &amp; Prescription of Percentage Charge) Rules, 1990, the value of which is also shown below and I engage to pay the percentage charges on excess value for increased risk as required by the Administration.</>],
    [4, <>Internal packing condition IP/- applicable<Choice k="c4_ip_status" options={["has been", "has not been"]} {...p} />complied with.</>],
    [5, <>Outer packing is defective as follows:<Blank k="c5_outer_packing_defect" w="w-64" placeholder="None" {...p} /></>],
    [6, <>The consignment is in bad condition as follows:<Blank k="c6_bad_condition" w="w-64" placeholder="None" {...p} /></>],
    [7, <>Compulsory packing condition IP/- and P/- applicable to the commodity<Blank k="c7_commodity" {...p} />have been complied with.</>],
    [8, <>Alternative railway risk and owner's risk rate being available, I elect to pay the<Blank k="c8_rate_election" placeholder="e.g. Owner's risk" {...p} />rates.</>],
    [9, <>To be forwarded via<Blank k="c9_route_via" {...p} />(dearer route) at my request.</>],
    [10, <>The cheapest route being closed / partially closed to be charged via<Blank k="c10_route_via" {...p} />the next cheapest open route.</>],
    [11, <>At my request, the consignment is despatched in<Choice k="c11_mode" options={["open wagon", "vehicle", "vessel"]} {...p} />.</>],
  ];
}

const EXPLANATORY = [
  ["Animal Liability", "Railway liability for animal loss or injury is capped per head (e.g., Rs. 6,000 for elephants down to Rs. 120 for smaller animals/birds) unless higher values are declared and paid for. The administration is not liable for fright, restiveness, or improper loading."],
  ["General Merchandise Liability", "Monetary liability is restricted to Rs. 100 per kg for personal baggage and Rs. 50 per kg for other goods unless higher declarations and percentage charges are made under the 1990 Rules."],
  ["Restricted Goods", "Schedule II Part-I items require mandatory value declarations and percentage charge payments."],
  ["Packing and Condition Records", "Specific declarations must be documented for internal compliance (4), defective outer packing (5), pre-existing bad condition (6), and mandatory compulsory packing compliance (7)."],
  ["Risk and Routing", "Owner's risk applies by default unless Railway risk is explicitly elected (8). Routing choices covering requested dearer routes (9) or alternative open routes due to closures (10) must likewise be specified."],
];

export function ForwardingNote({ note, onField, ack, onAck, onProceed }) {
  const [showExtra, setShowExtra] = useState(false);
  const [showNote, setShowNote] = useState(false);
  const list = clauses(note, onField).filter(([n]) => showExtra || !HIDDEN.includes(n));
  return (
    <div data-testid="pd-forwarding-note">
      <article className="border-2 border-border bg-card mt-4">
        <header className="border-b-2 border-border p-6 text-center">
          <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground flex items-center justify-center gap-2"><FileText className="h-3.5 w-3.5" /> Document</div>
          <h2 className="font-heading font-black text-2xl uppercase tracking-tight mt-1">Forwarding Note</h2>
          <p className="mt-4 text-sm italic leading-relaxed max-w-xl mx-auto" data-testid="fn-certification">
            "I do hereby certify that I have satisfied myself that the description, marks, and weight or quantity of goods consigned by me have been correctly entered in this Forwarding Note."
          </p>
        </header>

        <section className="p-6">
          <h3 className="text-xs font-bold uppercase tracking-widest text-primary mb-4">Declarations and Clauses</h3>
          <ol className="space-y-4 text-sm leading-7" data-testid="fn-clauses">
            {list.map(([n, body]) => (
              <li key={n} className="flex gap-3" data-testid={`fn-clause-${n}`}>
                <span className="font-mono-num font-bold text-primary shrink-0 w-6">{n}.</span>
                <span className="min-w-0">{body}</span>
              </li>
            ))}
          </ol>
          <button type="button" onClick={() => setShowExtra((v) => !v)} data-testid="fn-toggle-extra"
            className="mt-4 text-xs text-muted-foreground hover:text-primary underline inline-flex items-center gap-1">
            {showExtra ? "Hide additional railway/animal consignment clauses" : "Show additional railway/animal consignment clauses"}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${showExtra ? "rotate-180" : ""}`} />
          </button>
          <p className="mt-5 pt-4 border-t border-border text-sm leading-relaxed" data-testid="fn-section71">
            Notwithstanding the above, I agree to book the goods and pay freight by dearer route if the Railway Administration is authorised by the Central Govt. to do so under Section 71 of the Railways Act, 1989 (24 of 1989).
          </p>
        </section>

        <section className="border-t-2 border-border">
          <button type="button" onClick={() => setShowNote((v) => !v)} aria-expanded={showNote} data-testid="fn-toggle-explanatory"
            className="w-full flex items-center justify-between p-4 px-6 text-left hover:bg-muted/50 transition-colors">
            <span className="text-xs font-bold uppercase tracking-widest">Explanatory Note</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${showNote ? "rotate-180" : ""}`} />
          </button>
          {showNote && (
            <dl className="px-6 pb-6 space-y-3 text-sm text-muted-foreground" data-testid="fn-explanatory">
              {EXPLANATORY.map(([t, c]) => (
                <div key={t}><dt className="font-bold text-foreground">{t}:</dt><dd className="leading-relaxed">{c}</dd></div>
              ))}
            </dl>
          )}
        </section>
      </article>

      <label className="mt-6 flex items-start gap-3 cursor-pointer border border-border bg-card p-4" data-testid="fn-ack-label">
        <Checkbox checked={ack} onCheckedChange={(v) => onAck(!!v)} className="mt-0.5 rounded-none" data-testid="fn-ack" />
        <span className="text-sm font-medium">I have read and agree to the above Forwarding Note declarations.</span>
      </label>
      <div className="mt-6 flex justify-end">
        <Button type="button" disabled={!ack} onClick={onProceed} className="rounded-none gap-2 font-bold uppercase tracking-wide" data-testid="fn-proceed-payment">
          Proceed to Payment <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

export default ForwardingNote;
